import { DOCUMENT } from '@angular/common';
import { computed, effect, inject, Injectable, signal } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { map } from 'rxjs';

import { ThemeMode, UserPreferencesService } from '../user-preferences/user-preferences.service';
import { FeatureToggleService } from '../feature-toggle.service';
import { FeaturesConfig } from '../features-config';

const DARK_BODY_CLASS = 'theme-dark';
const PREFERS_DARK_QUERY = '(prefers-color-scheme: dark)';

@Injectable({
  providedIn: 'root',
})
export class ThemeService {
  private readonly document = inject(DOCUMENT);
  private readonly userPreferences = inject(UserPreferencesService);
  private readonly featureService = inject(FeatureToggleService);

  // System preference is reactive: if the user has 'system' selected and the OS
  // theme changes, the body class should follow without a reload.
  private readonly systemPrefersDark = signal(this.readSystemPrefersDark());

  // Re-reads the feature flag whenever an override changes (e.g. via the
  // window.featureToggles console helper), so toggling Theme.DarkMode at runtime
  // updates the theme without a reload. FeatureToggleService.updates is a
  // BehaviorSubject that emits on every override mutation.
  private readonly darkModeEnabled = toSignal(
    this.featureService.updates.pipe(map(() => this.featureService.isEnabled(FeaturesConfig.THEME_DARK_MODE))),
    { initialValue: this.featureService.isEnabled(FeaturesConfig.THEME_DARK_MODE) },
  );

  readonly themeMode = signal<ThemeMode>(this.userPreferences.getThemeMode());

  readonly resolvedTheme = computed<'light' | 'dark'>(() => {
    if (!this.darkModeEnabled()) {
      return 'light';
    }
    const mode = this.themeMode();
    if (mode === 'system') {
      return this.systemPrefersDark() ? 'dark' : 'light';
    }
    return mode;
  });

  constructor() {
    this.watchSystemPreference();

    effect(() => {
      const resolved = this.resolvedTheme();
      const body = this.document.body;
      if (!body) {
        return;
      }
      if (resolved === 'dark') {
        body.classList.add(DARK_BODY_CLASS);
      } else {
        body.classList.remove(DARK_BODY_CLASS);
      }
    });
  }

  setThemeMode(mode: ThemeMode): void {
    this.themeMode.set(mode);
    this.userPreferences.setThemeMode(mode);
  }

  private readSystemPrefersDark(): boolean {
    const win = this.document.defaultView;
    if (!win?.matchMedia) {
      return false;
    }
    return win.matchMedia(PREFERS_DARK_QUERY).matches;
  }

  private watchSystemPreference(): void {
    const win = this.document.defaultView;
    if (!win?.matchMedia) {
      return;
    }
    const mql = win.matchMedia(PREFERS_DARK_QUERY);
    // addEventListener is widely supported; addListener is the deprecated fallback.
    mql.addEventListener('change', (event) => {
      this.systemPrefersDark.set(event.matches);
    });
  }
}
